/**
 * Photos taken at the counter (T07-31 / T07-32, design/08 §3):
 *
 * | what | where | how |
 * |---|---|---|
 * | ID document (本人確認) / guardian's consent | media-service `POST /pos/media/restricted` (purpose `identity_document`) | multipart, **encrypted at rest, private** — the image never passes through pos-service; only the returned asset id goes into `POST /pos/identity-checks` |
 * | card front / back (optional) | media-service `POST /pos/media/uploads` → `PUT` presigned → `…/complete` | the product photo pipeline |
 *
 * A photo is shrunk to ≤ 1600 px JPEG on the tablet first (a 12 MP camera shot is 4–6 MB; the
 * restricted upload accepts ≤ 10 MiB) — when the browser cannot (no canvas) the original is sent.
 */
import type { MediaClient } from "@tcg/api-client";

import { call } from "./api";
import type { RestrictedAsset } from "./consignment-types";
import { ApiError } from "./errors";
import { newIdempotencyKey } from "./idempotency";

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const MAX_EDGE_PX = 1600;

export type RestrictedPurpose = "identity_document";

export interface UploadedPhoto {
  assetId: string;
  bytes: number;
  mime: string | null;
}

/** Shrink a camera shot (keeps small files as they are). */
export async function shrinkPhoto(file: Blob, maxEdge = MAX_EDGE_PX): Promise<Blob> {
  if (!file.type.startsWith("image/") || typeof createImageBitmap !== "function") {
    return file;
  }
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    if (scale >= 1 && file.size <= 2 * 1024 * 1024) {
      bitmap.close();
      return file;
    }
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      bitmap.close();
      return file;
    }
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.85),
    );
    return blob ?? file;
  } catch {
    return file;
  }
}

function fileName(blob: Blob, fallback: string): string {
  return blob instanceof File && blob.name ? blob.name : fallback;
}

/**
 * The ID photo → media-service's encrypted upload (never pos-service). Answers the asset id the
 * 本人確認 record points to.
 */
export async function uploadRestricted(
  media: MediaClient,
  photo: Blob,
  purpose: RestrictedPurpose = "identity_document",
): Promise<UploadedPhoto> {
  const blob = await shrinkPhoto(photo);
  if (blob.size > MAX_UPLOAD_BYTES) {
    throw new ApiError(413, "file_too_large", "", [{ maxBytes: MAX_UPLOAD_BYTES }]);
  }
  const name = fileName(photo, "id-document.jpg");
  const asset: RestrictedAsset = await call(() =>
    media.POST("/v1/pos/media/restricted", {
      body: { file: name, purpose },
      bodySerializer: () => {
        const form = new FormData();
        form.append("purpose", purpose);
        form.append("file", blob, name);
        return form;
      },
      headers: { "Idempotency-Key": newIdempotencyKey() },
    }),
  );
  return { assetId: asset.assetId, bytes: asset.bytes, mime: asset.mime ?? null };
}

/** A card photo (front / back) through the presigned product-photo upload. */
export async function uploadCardPhoto(
  media: MediaClient,
  photo: Blob,
  fetchImpl: typeof fetch = fetch,
): Promise<UploadedPhoto> {
  const blob = await shrinkPhoto(photo);
  const mime = blob.type || "image/jpeg";
  const ticket = await call(() =>
    media.POST("/v1/pos/media/uploads", {
      body: {
        filename: fileName(photo, "card.jpg"),
        mime,
        bytes: blob.size,
        purpose: "product_image",
      },
      headers: { "Idempotency-Key": newIdempotencyKey() },
    }),
  );
  let put: Response;
  try {
    put = await fetchImpl(ticket.url, { method: "PUT", headers: ticket.headers, body: blob });
  } catch (cause) {
    throw ApiError.network(cause);
  }
  if (!put.ok) {
    throw new ApiError(put.status, "upload_failed", "");
  }
  await call(() =>
    media.POST("/v1/pos/media/uploads/{asset_id}/complete", {
      params: { path: { asset_id: ticket.assetId } },
    }),
  );
  return { assetId: ticket.assetId, bytes: blob.size, mime };
}
